import React, { useEffect, useMemo, useState } from 'react';
import { FaBell, FaChartLine, FaEye, FaShareAlt } from 'react-icons/fa';
import OddsDetailsDialog from './oddsDetailsDialog';
import logo555Mix from "../../../assets/icons/555mix-logo.avif";
import logo909 from "../../../assets/icons/909.png";

const BookmakerOdds = ({ variant, data }) => {
  if (!data) {
    return null;
  }

  return (
    <div
      className={`flex flex-col gap-1 rounded-md border shadow-md px-2 py-1 h-[100px] ${
        variant === 'base'
          ? 'border-customSlate bg-customSlate/90 dark:border-custom_dark_secondary/60 dark:bg-custom_dark_primary'
          : 'border-customLightMilkWhite bg-white dark:border-custom_dark_secondary/60 dark:bg-custom_dark_primary2'
      }`}
    >
      <div className="flex flex-col justify-between gap-1">
        <div className="flex flex-col">
          <div className='flex items-center gap-2'>
            <img src={data.bookmaker === "555Mix" ? logo555Mix : logo909} alt="" width={data.bookmaker === "555Mix" ? 15 : 12} height={'auto'} />
            <span className={`text-xs font-semibold ${data.bookmaker === "555Mix" ? 'text-customDarkGreen dark:text-emerald-300' : 'text-customAccent dark:text-customLightYellow'}`}>
              {data.bookmaker}
            </span>
          </div>
          <span className="text-paragraph antialiased text-customPrimaryText dark:text-customFontMilkWhite/70 font-semibold">
            {data.matchup}
          </span>
        </div>
        {/* <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
            variant === 'base' 
              ? 'bg-customSky text-customAccentBlue dark:bg-custom_dark_secondary/40 dark:text-customFontLightGreen'
              : 'bg-customDarkGreen/10 text-customDarkGreen dark:bg-custom_dark_primary/40 dark:text-customFontMilkWhite'
          }`}
        >
          {variant === 'base' ? 'Base' : 'Reference'}
        </span> */}
        {data.burmese_handicap ? (
              <div className="flex justify-between gap-1">
                <span className="rounded-md border border-customLightMilkWhite shadow-lg bg-white px-2 py-1 text-sm font-semibold text-customPredawn dark:border-custom_dark_secondary dark:bg-custom_dark_background dark:text-customWhite">
                  Body {data.burmese_handicap.body}
                </span>
                <span className="rounded-md border border-customLightMilkWhite shadow-lg bg-white px-2 py-1 text-sm font-semibold text-customPredawn dark:border-custom_dark_secondary dark:bg-custom_dark_background dark:text-customWhite">
                  Goal {data.burmese_handicap.goal}
                </span>
              </div>
            ) : (
              <span className="text-xs text-customMuted dark:text-customFontMilkWhite/50">N/A</span>
            )}
      </div>
    </div>
  );
};

const ACTION_ITEMS = [
  { id: 'details', label: 'View details', icon: FaEye },
  { id: 'movement', label: 'Track movement', icon: FaChartLine },
  { id: 'alerts', label: 'Set alert', icon: FaBell },
  { id: 'share', label: 'Share odds', icon: FaShareAlt },
];

const ActionArea = ({ onViewDetails }) => {
  const handleAction = (id) => {
    if (id === 'details') {
      onViewDetails();
      return;
    }

    alert("coming soon!");
  };

  return (
    <div className="px-2 pb-2 pt-1">
      <div className="flex items-center justify-between rounded-2xl border border-customSlate/80 bg-gradient-to-r from-white via-customSlate/50 to-customSky/40 px-2 py-1 shadow-sm dark:border-custom_dark_secondary dark:from-custom_dark_primary2 dark:via-custom_dark_primary dark:to-custom_dark_background">
        {ACTION_ITEMS.map(({ id, label, icon: Icon }) => (
          <div key={id} className="group relative">
            <button
              type="button"
              onClick={() => handleAction(id)}
              className="flex h-8 w-8 items-center justify-center rounded-xl border border-customAccent/30 bg-white/80 text-customAccent shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-customAccent/20 hover:bg-white hover:text-customAccentBlue hover:shadow-md focus:outline-none focus:ring-2 focus:ring-customAccent/30 dark:border-custom_dark_secondary dark:bg-custom_dark_primary dark:text-customWhite dark:hover:border-customAccent/30 dark:hover:bg-custom_dark_background"
              aria-label={label}
              title={label}
            >
              <Icon className="text-sm" />
            </button>
            <span className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 -translate-x-1/2 rounded-md bg-customPredawn px-2 text-[10px] font-medium text-white opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 dark:bg-custom_dark_primary">
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const OddsCard = ({ league, kickoff, base, reference, isHighlighted, highlightAnimationName }) => {
  const [activeReferenceId, setActiveReferenceId] = useState(reference?.[0]?.id ?? null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  useEffect(() => {
    setActiveReferenceId(reference?.[0]?.id ?? null);
  }, [reference]);

  const activeReference = useMemo(
    () => reference?.find((item) => item.id === activeReferenceId) ?? reference?.[0],
    [reference, activeReferenceId]
  );

  return (
    <div
      className={`z-0 relative flex h-full flex-col rounded-lg border bg-white dark:bg-custom_dark_primary2 shadow-sorting transition-all ${
              isHighlighted
          ? 'border-orange-400 ring-2 ring-orange-300/80 dark:border-orange-300 dark:ring-orange-400/40'
          : 'border-customSlate dark:border-custom_dark_secondary'
      }`}
      style={isHighlighted ? { animation: `${highlightAnimationName} 1s ease-in-out 5` } : undefined}
    >
      <div className="flex items-start justify-between px-3 pt-2">
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-customPrimaryText/80 dark:text-customMuted">{league}</span>
          <span className="text-xs font-normal text-customSecondaryText dark:text-customMuted my-1">{kickoff}</span>
        </div>
        {/* <div className="flex items-center gap-1">
          <div className="text-[10px] uppercase text-customMuted dark:text-customFontMilkWhite/60">Odds Hub</div>
          <button
            type="button"
            onClick={() => setIsDetailsDialogOpen(true)}
            className="rounded p-1 text-customAccentBlue transition-colors hover:bg-customSky dark:text-customFontMilkWhite dark:hover:bg-custom_dark_secondary/40"
            aria-label="View full odds details"
          >
            <FaEye className="h-3 w-3" />
          </button>
        </div> */}
      </div>
      <div className="flex flex-1 flex-col divide-y divide-customLightMilkWhite dark:divide-custom_dark_secondary">
        <div className="flex flex-col gap-2 px-2 pb-1 dark:bg-custom_dark_primary">
          {base?.length ? (
            base.map((bookmaker) => <BookmakerOdds key={bookmaker.id} variant="base" data={bookmaker} />)
          ) : (
            <span className="text-xs text-customMuted dark:text-customFontMilkWhite/50">Base odds unavailable</span>
          )}
        </div>
        <div className="flex flex-col gap-2 bg-customSlate/40 px-2 py-1 rounded-b-lg dark:bg-custom_dark_background">
          {/* <div className="flex flex-wrap gap-1">
            {reference?.map((bookmaker) => (
              <button
                key={bookmaker.id}
                type="button"
                onClick={() => setActiveReferenceId(bookmaker.id)}
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors shadow-sm ${
                  activeReferenceId === bookmaker.id
                    ? 'bg-customAccent text-white dark:text-customFontMilkWhite'
                    : 'bg-white text-customSecondaryText border border-custom_dark_secondary/30 dark:bg-custom_dark_primary2 dark:text-customFontMilkWhite/70'
                }`}
              >
                {bookmaker.bookmaker}
              </button>
            ))}
          </div> */}
          {activeReference ? (
            <BookmakerOdds variant="reference" data={activeReference} />
          ) : (
            <span className="text-xs text-customMuted dark:text-customFontMilkWhite/50">
              Select a bookmaker to view odds
            </span>
          )}
        </div>
        <ActionArea onViewDetails={() => setIsDetailsDialogOpen(true)} />
      </div>
      <OddsDetailsDialog
        open={isDetailsDialogOpen}
        onClose={() => setIsDetailsDialogOpen(false)}
        league={league}
        kickoff={kickoff}
        base={base}
        reference={reference}
      />
    </div>
  );
};

export default OddsCard;
